import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { X, BrainCircuit, Trash2 } from "lucide-react";

const PROBLEM_TYPES = [
  { value: "classification", label: "Classification (predicts a category)" },
  { value: "regression", label: "Regression (predicts a number)" },
];

const IMPUTATION_STRATEGIES = ["mean", "median", "most_frequent", "constant", "drop"];
const AGG_FUNCTIONS = ["count", "sum", "avg", "max", "min"];

const emptyFeature = () => ({
  feature_name: "", mode: "direct", source_field: "",
  agg_source_table: "", agg_filter_field: "", agg_time_field: "", agg_window_days: 30, agg_function: "count", agg_value_field: "",
  imputation_strategy: "mean", imputation_constant: "",
});

const ModelTrainingWizard = ({ isOpen, onClose, apiBaseUrl, token, onComplete }) => {
  const [phase, setPhase] = useState("target"); // target -> features -> profile -> train -> done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [tables, setTables] = useState([]);
  const [targetFields, setTargetFields] = useState([]);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [problemType, setProblemType] = useState("classification");
  const [targetTableId, setTargetTableId] = useState("");
  const [labelMode, setLabelMode] = useState("field"); // field | script
  const [targetField, setTargetField] = useState("");
  const [labelScript, setLabelScript] = useState("");

  const [modelId, setModelId] = useState(null);
  const [features, setFeatures] = useState([emptyFeature()]);
  const [savedFeatures, setSavedFeatures] = useState([]);

  const [profile, setProfile] = useState(null);
  const [trainResult, setTrainResult] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${apiBaseUrl}/tables/?limit=300`, { headers: authHeaders() })
      .then((res) => setTables(res.data.records || res.data))
      .catch(() => setTables([]));
  }, [isOpen]);

  useEffect(() => {
    if (!targetTableId) { setTargetFields([]); return; }
    axios.get(`${apiBaseUrl}/search/table_fields/?table_id__eq=${targetTableId}&limit=200`, { headers: authHeaders() })
      .then((res) => setTargetFields(res.data.records || []))
      .catch(() => setTargetFields([]));
  }, [targetTableId]);

  if (!isOpen) return null;

  const reset = () => {
    setPhase("target"); setBusy(false); setError("");
    setName(""); setLabel(""); setProblemType("classification"); setTargetTableId("");
    setLabelMode("field"); setTargetField(""); setLabelScript("");
    setModelId(null); setFeatures([emptyFeature()]); setSavedFeatures([]);
    setProfile(null); setTrainResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const createModel = async () => {
    if (!name || !targetTableId || (labelMode === "field" && !targetField) || (labelMode === "script" && !labelScript)) {
      setError("Fill in name, target table, and how to determine the label.");
      return;
    }
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${apiBaseUrl}/ml_models/`, {
        name, label: label || name, problem_type: problemType, target_table_id: targetTableId,
        target_field: labelMode === "field" ? targetField : null,
        label_script: labelMode === "script" ? labelScript : null,
        status: "draft",
      }, { headers: authHeaders() });
      setModelId(res.data.record.id);
      setPhase("features");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create the model.");
    } finally {
      setBusy(false);
    }
  };

  const updateFeature = (idx, patch) => {
    setFeatures((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const addFeature = () => setFeatures((prev) => [...prev, emptyFeature()]);
  const removeFeature = (idx) => setFeatures((prev) => prev.filter((_, i) => i !== idx));

  const saveFeaturesAndProfile = async () => {
    setBusy(true); setError("");
    try {
      const created = [];
      for (const f of features) {
        if (!f.feature_name) continue;
        const body = {
          model_id: modelId,
          feature_name: f.feature_name,
          imputation_strategy: f.imputation_strategy,
          imputation_constant: f.imputation_strategy === "constant" ? f.imputation_constant : null,
        };
        if (f.mode === "direct") {
          body.source_field = f.source_field;
        } else {
          body.aggregation_definition = {
            source_table: f.agg_source_table, filter_field: f.agg_filter_field,
            time_field: f.agg_time_field || null, window_days: f.agg_window_days || null,
            function: f.agg_function, value_field: f.agg_function === "count" ? null : f.agg_value_field,
          };
        }
        const res = await axios.post(`${apiBaseUrl}/ml_model_features/`, body, { headers: authHeaders() });
        created.push(res.data.record);
      }
      setSavedFeatures(created);
      const profileRes = await axios.post(`${apiBaseUrl}/ml-models/${modelId}/profile`, {}, { headers: authHeaders() });
      setProfile(profileRes.data);
      setPhase("profile");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save features / profile the data.");
    } finally {
      setBusy(false);
    }
  };

  const updateImputation = async (featureId, strategy) => {
    setSavedFeatures((prev) => prev.map((f) => (f.id === featureId ? { ...f, imputation_strategy: strategy } : f)));
    try {
      await axios.put(`${apiBaseUrl}/ml_model_features/${featureId}`, { imputation_strategy: strategy }, { headers: authHeaders() });
    } catch { /* best-effort; training will still use whatever was last saved */ }
  };

  const runTraining = async () => {
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${apiBaseUrl}/ml-models/${modelId}/train`, {}, { headers: authHeaders() });
      setTrainResult(res.data);
      setPhase("done");
      onComplete && onComplete();
    } catch (e) {
      setError(e.response?.data?.detail || "Training failed.");
      setPhase("done");
      setTrainResult({ status: "failed", error: e.response?.data?.detail || "Training failed." });
    } finally {
      setBusy(false);
    }
  };

  const stepLabel = { target: "1. Define target", features: "2. Define features", profile: "3. Profile data", done: "4. Train & compare" }[phase];

  return ReactDOM.createPortal(
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={styles.headerIcon}><BrainCircuit size={16} /></span>
            <span style={styles.crumb}>Train a Prediction Model</span>
            <span style={styles.sep}>·</span>
            <span style={styles.crumb}>{stepLabel}</span>
          </div>
          <button onClick={handleClose} style={styles.iconBtn} title="Close"><X size={16} /></button>
        </div>

        <div style={styles.body}>
          {error && <div style={styles.errorBanner}>{error}</div>}

          {phase === "target" && (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={styles.row2}>
                <div>
                  <div style={styles.label}>Model name (unique)</div>
                  <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="truck_breakdown_risk" />
                </div>
                <div>
                  <div style={styles.label}>Display label</div>
                  <input style={styles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Truck Breakdown Risk" />
                </div>
              </div>

              <div>
                <div style={styles.label}>Problem type</div>
                <select style={styles.select} value={problemType} onChange={(e) => setProblemType(e.target.value)}>
                  {PROBLEM_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              <div>
                <div style={styles.label}>Target table (what are we predicting about?)</div>
                <select style={styles.select} value={targetTableId} onChange={(e) => setTargetTableId(e.target.value)}>
                  <option value="">Select a table...</option>
                  {tables.map((t) => <option key={t.id} value={t.id}>{t.label || t.name}</option>)}
                </select>
              </div>

              <div>
                <div style={styles.label}>How is the label (what to predict) determined?</div>
                <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                  <label style={styles.radioLabel}>
                    <input type="radio" checked={labelMode === "field"} onChange={() => setLabelMode("field")} /> An existing field
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" checked={labelMode === "script"} onChange={() => setLabelMode("script")} /> Custom script (e.g. time-windowed)
                  </label>
                </div>
                {labelMode === "field" ? (
                  <select style={styles.select} value={targetField} onChange={(e) => setTargetField(e.target.value)} disabled={!targetFields.length}>
                    <option value="">Select a field...</option>
                    {targetFields.map((f) => <option key={f.id} value={f.name}>{f.label || f.name}</option>)}
                  </select>
                ) : (
                  <textarea
                    style={styles.textarea}
                    value={labelScript}
                    onChange={(e) => setLabelScript(e.target.value)}
                    placeholder={"# 'record' is the row; set label to the value being predicted\nlabel = 1 if record.some_field else 0"}
                  />
                )}
              </div>
            </div>
          )}

          {phase === "features" && (
            <div style={{ display: "grid", gap: 16 }}>
              <p style={styles.hint}>Add each input the model should learn from. A direct field, or an aggregate over a related table (e.g. "count of harsh-braking events, last 30 days").</p>
              {features.map((f, idx) => (
                <div key={idx} style={styles.featureCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <input
                      style={{ ...styles.input, maxWidth: 260 }}
                      value={f.feature_name}
                      onChange={(e) => updateFeature(idx, { feature_name: e.target.value })}
                      placeholder="feature_name"
                    />
                    <button onClick={() => removeFeature(idx)} style={styles.iconBtnLight} title="Remove"><Trash2 size={14} /></button>
                  </div>
                  <div style={{ display: "flex", gap: 16, margin: "8px 0" }}>
                    <label style={styles.radioLabel}><input type="radio" checked={f.mode === "direct"} onChange={() => updateFeature(idx, { mode: "direct" })} /> Direct field</label>
                    <label style={styles.radioLabel}><input type="radio" checked={f.mode === "aggregation"} onChange={() => updateFeature(idx, { mode: "aggregation" })} /> Aggregation</label>
                  </div>
                  {f.mode === "direct" ? (
                    <select style={styles.select} value={f.source_field} onChange={(e) => updateFeature(idx, { source_field: e.target.value })}>
                      <option value="">Select a field...</option>
                      {targetFields.map((tf) => <option key={tf.id} value={tf.name}>{tf.label || tf.name}</option>)}
                    </select>
                  ) : (
                    <div style={styles.row2}>
                      <input style={styles.input} placeholder="source table (e.g. incident_events)" value={f.agg_source_table} onChange={(e) => updateFeature(idx, { agg_source_table: e.target.value })} />
                      <input style={styles.input} placeholder="filter field (links back to target row id)" value={f.agg_filter_field} onChange={(e) => updateFeature(idx, { agg_filter_field: e.target.value })} />
                      <input style={styles.input} placeholder="time field (optional)" value={f.agg_time_field} onChange={(e) => updateFeature(idx, { agg_time_field: e.target.value })} />
                      <input style={styles.input} type="number" placeholder="window (days)" value={f.agg_window_days} onChange={(e) => updateFeature(idx, { agg_window_days: e.target.value })} />
                      <select style={styles.select} value={f.agg_function} onChange={(e) => updateFeature(idx, { agg_function: e.target.value })}>
                        {AGG_FUNCTIONS.map((fn) => <option key={fn} value={fn}>{fn}</option>)}
                      </select>
                      {f.agg_function !== "count" && (
                        <input style={styles.input} placeholder="value field to aggregate" value={f.agg_value_field} onChange={(e) => updateFeature(idx, { agg_value_field: e.target.value })} />
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <span style={{ ...styles.label, display: "inline-block", marginRight: 8 }}>If missing:</span>
                    <select style={styles.selectSm} value={f.imputation_strategy} onChange={(e) => updateFeature(idx, { imputation_strategy: e.target.value })}>
                      {IMPUTATION_STRATEGIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {f.imputation_strategy === "constant" && (
                      <input style={{ ...styles.input, display: "inline-block", width: 120, marginLeft: 8 }} placeholder="value" value={f.imputation_constant} onChange={(e) => updateFeature(idx, { imputation_constant: e.target.value })} />
                    )}
                  </div>
                </div>
              ))}
              <button onClick={addFeature} style={styles.btnGhost}>+ Add another feature</button>
            </div>
          )}

          {phase === "profile" && profile && (
            <div style={{ display: "grid", gap: 14 }}>
              <p style={styles.hint}>{profile.row_count} labeled training rows found. Review missing-data rates and adjust how gaps get filled before training.</p>
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Feature</th><th style={styles.th}>Missing %</th><th style={styles.th}>Distinct</th><th style={styles.th}>Samples</th><th style={styles.th}>Fill with</th></tr></thead>
                <tbody>
                  {Object.entries(profile.features).map(([fname, stats]) => {
                    const saved = savedFeatures.find((f) => f.feature_name === fname);
                    return (
                      <tr key={fname}>
                        <td style={styles.td}>{fname}</td>
                        <td style={styles.td}>{stats.missing_pct}%</td>
                        <td style={styles.td}>{stats.distinct_count}</td>
                        <td style={styles.td}>{stats.sample_values.join(", ")}</td>
                        <td style={styles.td}>
                          {saved && (
                            <select style={styles.selectSm} value={saved.imputation_strategy} onChange={(e) => updateImputation(saved.id, e.target.value)}>
                              {IMPUTATION_STRATEGIES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {phase === "done" && trainResult && (
            <div style={{ display: "grid", gap: 14 }}>
              {trainResult.status === "failed" ? (
                <div style={styles.errorBanner}>{trainResult.error}</div>
              ) : (
                <>
                  <p style={styles.hint}>Trained on {trainResult.training_row_count} rows. Winner: <strong>{trainResult.selected_algorithm}</strong></p>
                  <table style={styles.table}>
                    <thead><tr><th style={styles.th}>Algorithm</th><th style={styles.th}>Metrics</th></tr></thead>
                    <tbody>
                      {trainResult.candidates_tried.map((c, i) => (
                        <tr key={i} style={c.algorithm === trainResult.selected_algorithm ? { background: "#e8f5f3" } : undefined}>
                          <td style={styles.td}>{c.algorithm}{c.algorithm === trainResult.selected_algorithm ? " (active)" : ""}</td>
                          <td style={styles.td}>{c.error ? `Error: ${c.error}` : Object.entries(c.metrics).map(([k, v]) => `${k}=${v}`).join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {phase !== "done" && <button style={styles.btnGhost} onClick={handleClose} disabled={busy}>Cancel</button>}
          {phase === "target" && <button style={styles.btnPrimary} onClick={createModel} disabled={busy}>{busy ? "Working..." : "Continue"}</button>}
          {phase === "features" && <button style={styles.btnPrimary} onClick={saveFeaturesAndProfile} disabled={busy}>{busy ? "Working..." : "Continue to Profile"}</button>}
          {phase === "profile" && <button style={styles.btnPrimary} onClick={runTraining} disabled={busy}>{busy ? "Training..." : "Train & Compare"}</button>}
          {phase === "done" && <button style={styles.btnPrimary} onClick={handleClose}>Close</button>}
        </div>
      </div>
    </div>,
    document.body
  );
};

const styles = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(8, 20, 19, 0.6)", backdropFilter: "blur(2px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 16 },
  modal: { backgroundColor: "#ffffff", width: "94vw", maxWidth: 960, maxHeight: "90vh", display: "flex", flexDirection: "column", borderRadius: 20, boxShadow: "0 24px 60px rgba(8,95,84,.18)", overflow: "hidden" },
  header: { padding: "14px 18px", borderBottom: "1px solid #dfe7e5", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(90deg, #085f54, #0d7d6f)", color: "#e5e7eb" },
  headerIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.14)", color: "#fff" },
  crumb: { fontWeight: 600, opacity: 0.9, color: "#e6f4f1" },
  sep: { opacity: 0.5 },
  body: { padding: 18, overflowY: "auto", flex: 1 },
  footer: { padding: 12, borderTop: "1px solid #dfe7e5", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 8 },
  iconBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "1px solid rgba(229,231,235,.4)", color: "#e5e7eb", borderRadius: 10, padding: "6px 10px", cursor: "pointer" },
  iconBtnLight: { display: "inline-flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "1px solid #dfe7e5", color: "#991b1b", borderRadius: 8, padding: "5px 8px", cursor: "pointer" },
  btnPrimary: { border: "1px solid #0d7d6f", background: "#0d7d6f", color: "#fff", padding: "10px 14px", borderRadius: 10, fontWeight: 800, cursor: "pointer" },
  btnGhost: { border: "1px solid #dfe7e5", background: "#fff", color: "#1e2e30", padding: "10px 14px", borderRadius: 10, fontWeight: 700, cursor: "pointer" },
  input: { padding: "8px 10px", borderRadius: 8, border: "1px solid #dfe7e5", background: "#fff", outline: "none", width: "100%" },
  select: { padding: "8px 10px", borderRadius: 8, border: "1px solid #dfe7e5", background: "#fff", outline: "none", width: "100%" },
  selectSm: { padding: "5px 8px", borderRadius: 6, border: "1px solid #dfe7e5", background: "#fff", outline: "none", fontSize: 13 },
  textarea: { width: "100%", minHeight: 90, padding: "8px 10px", borderRadius: 8, border: "1px solid #dfe7e5", fontFamily: "monospace", fontSize: 13 },
  label: { fontSize: 11, fontWeight: 700, color: "#5c6d6f", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 },
  hint: { fontSize: 13, color: "#5c6d6f", margin: 0 },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  radioLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" },
  featureCard: { border: "1px solid #dfe7e5", borderRadius: 10, padding: 14, background: "#fafcfb" },
  errorBanner: { background: "#fef2f2", color: "#991b1b", padding: "10px 12px", borderRadius: 10, fontSize: 13, marginBottom: 12 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "#5c6d6f", padding: "6px 8px", borderBottom: "1px solid #dfe7e5" },
  td: { padding: "8px", borderBottom: "1px solid #eef2f1", fontSize: 13, verticalAlign: "top" },
};

export default ModelTrainingWizard;
